import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { DateValue, toZoned } from '@internationalized/date';
import { useAirports } from '@lp/features/RoutePlanner/hooks/useAirports';
import { BaseCommercialAviaLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { validateDateTimeValue } from '@/utils/helpers';

type UseConnectedLegDetailsProps = {
  type: 'departureLocation' | 'arrivalLocation';
  index: number;
  defaultId?: string;
  defaultValue?: string;
};

export const useConnectedLegDetails = ({ type, index, defaultId, defaultValue }: UseConnectedLegDetailsProps) => {
  const { setValue } = useFormContext<BaseCommercialAviaLegSchemaValues>();

  const {
    airportsList,
    onAirportSearch,
    setAirportsAutocompleteOpen,
    selectedAirport,
    searchValue,
    ...airportListProps
  } = useAirports({
    selectedId: defaultId,
    selectedValue: defaultValue,
    setValue: (v: string) => setValue(`connectedLegs.${index}.${type}.id`, v, { shouldValidate: true }),
  });

  useEffect(() => {
    if (selectedAirport) {
      setValue(`connectedLegs.${index}.${type}.maoId`, selectedAirport.maoId);
      setValue(`connectedLegs.${index}.${type}.displayName`, selectedAirport.name);
      setValue(`connectedLegs.${index}.${type}.timezone`, selectedAirport.location.timezone);

      const dateTimeZoneKey = type === 'departureLocation' ? 'departureLocalTimezone' : 'arrivalLocalTimezone';
      setValue(`connectedLegs.${index}.${dateTimeZoneKey}`, selectedAirport.location.timezone);
    }
  }, [setValue, selectedAirport, type, index]);

  const onAirportListOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen && !airportsList.length) {
        onAirportSearch('');
      }
      setAirportsAutocompleteOpen(true);
    },
    [airportsList.length, onAirportSearch, setAirportsAutocompleteOpen],
  );

  const onDateTimeChange = useCallback(
    (date: DateValue, timezone: string): string => {
      const dateType = type === 'departureLocation' ? 'departure' : 'arrival';
      const dateValue = validateDateTimeValue(date);
      const zonedDateValue = dateValue ? toZoned(dateValue, timezone) : date;
      const dateString = dateValue ? zonedDateValue.toString() : null;

      setValue(`connectedLegs.${index}.${dateType}DateTimeValue`, zonedDateValue);
      setValue(`connectedLegs.${index}.${dateType}DateTime`, dateString || '');

      return dateString || '';
    },
    [setValue, type, index],
  );

  return {
    airportsList,
    selectedAirport,
    onAirportSearch,
    setAirportsAutocompleteOpen,
    onAirportListOpenChange,
    onDateTimeChange,
    searchValue,
    ...airportListProps,
  };
};
